import React, { useEffect, useState, useMemo, Fragment, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, RootState } from '../../store';
import { useSelector } from 'react-redux';
import { showModal, hideModal, handleAddCollaborator } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import {
  PiFileTextLight,
  PiCheckCircleLight,
  PiDotsThreeVerticalLight,
  PiTrashLight,
  PiNoteLight,
  PiQuestionMarkLight,
  PiFileLight,
  PiLightbulbLight,
  PiSparkleLight,
  PiUsersLight,
  PiCheckSquareOffsetLight,
  PiLightningLight,
  PiSpinnerLight,
  PiWarningLight,
  PiClockCounterClockwiseLight,
  PiLockSimpleLight,
} from 'react-icons/pi';
import { getUserType, isBetaEnv } from '../../utils/authUtils';
import { convertLongDateWithTranslation } from '../shared/DateConverter';
import ReactCountryFlag from 'react-country-flag';
import { getCode } from 'country-list';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { addMatterCollaborators, removeMatterCollaborator, updateMatterCollaboratorRole } from '../../store/actions/Matters/MatterActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Action } from '../shared/TailwindComponents/Header';
import DetailFrame from '../shared/Frames/DetailFrame';
import MatterDetailsContent from './MatterDetailsContent';
import DeleteMatterConfirmation from './DeleteMatterConfirmation';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import MatterTabBar, { TabItem } from './MatterTabBar';
import { fetchMatterSources, fetchQuestionnaire, fetchMatterNotes, deleteMatter, editMatter, grantClientAccess, clearPrefetchedMatterData, fetchMatterDetails, fetchSuggestedTasks, analyzeMatter, getAnalysisStatus } from '../../store/actions/Matters/MatterActions';
import { fetchAllTasks } from '../../store/actions/TasksActions';
import apis from '../../utils/apis';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import { formatInsightName, formatDate } from '../../utils/functions';
import { getJurisdictionDisplay } from './Matters';
import Flag from '../shared/TailwindComponents/Flag';
import ProgressTracker, { Step } from './ProgressTracker';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { hasCasesSupport } from './matterConstants';
import IndexSourceSelector from './IndexSourceSelector';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import AnalyzeWizardModal, { AnalyzeWizardResult } from './AnalyzeWizardModal';
import { FeatureTour, matterDetailOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour, clearActiveTourLock } from '../../hooks/useFeatureTour';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';

export interface MatterData {
  id: string;
  uuid: string;
  MatterID: string;
  MatterName: string;
  MatterType: string;
  UserID: string;
  status: string;
  Description: string;
  Notes: string; 
  fields: {
    bulk_files: Array<{
      label: string;
      content: string;
      source_id: string;
    }>;
    Language: string;
    FetchApplicableLaws: string;
    AI_Description: string;
  };
  created_at: string;
  updated_at?: string;
  DateOpened: string;
  DateClosed: null | string;
  Jurisdiction: string;
  jurisdiction: string;
  language?: string;
  Language?: string;
  due_date?: string | null;
  DueDate?: string | null;
  questionnaire: Array<{
    question: string;
    potential_answer: string;
    who_answered: string;
    when_answered: string;
    status: 'pending';
    client_assigned: boolean;
  }>;
  MatterStage: string;
  Clients: Array<{
    ClientID: string;
    ClientName: string;
    ClientType: string;
    Roles: string[];
    IDVerificationStatus: string;
    ContactDetails: Record<string, any>;
    UserID: string | null;
  }>;
  OpposingParties: any[];
  Attorneys: any[];
  Collaborators: Array<{
    UserID: number;
    FullName: string;
    Email: string;
    Permission: 'write';
    SharedBy: string;
    SharedAt: string;
    Role: string;
  }>;
  BillingStatus: string;
  InitialTasks: any[];
  Processes: any[];
  RetrievedLaws: {
    response: string;
    citations: any[];
  };
  RetrievedCases: {
    response: string;
    citations: any[];
  };
  is_analysis_run: boolean;
  FinancialData: Array<{
    label: string;
    value: string;
    type: 'short_text';
  }>;
  is_deleted: boolean;
  processing_status?: string;
  processing_correlation_id?: string;
  last_analyzed_at?: string;
  MatterHistory: Array<{
    action: string;
    performed_by: string;
    details: string;
    timestamp: string;
  }>;
  LawyerID: string;
  LawyerName: string;
  OrganizationID: string;
  OrganizationName: string;
  MatterFolderID: string;
  current_stage_id?: number;
  current_status_id?: number;
  category_id?: number;
  category_name?: string;
  category_description?: string;
  stage_id?: number;
  stage_name?: string;
  status_id?: number;
  status_name?: string;
  collaborators?: Array<{
    access: string;
    avatar_url: string | null;
    collaborator_id: string;
    email: string;
    initials: string | null;
    matter_id: string;
    name: string;
    role: string;
    shared_at: string;
    shared_by: string;
    shared_by_user?: {
      email: string;
      first_name: string;
      full_name: string;
      last_name: string;
      user_id: number;
      uuid: string;
    };
  }>;
  matter_clients?: {
    clients: Array<{
      added_at: string;
      added_by_user_id: number;
      client_id: number;
      client_uuid: string;
      contact_phone: string;
      email: string;
      name: string;
      role: string;
      role_id: string;
      role_name: string;
    }>;
    count: number;
    created_at: string;
    created_by_user_id: number;
    matter_uuid: string;
  };
}

interface RouteParams {
  matterId: string; 
  draftId: string;
  insightId?: string; 
}


interface DataState {
  data: MatterData | null;
  lastFetched: number | null;
  isStale: boolean;
  error: string | null;
  hasTriedLoading: boolean;
}

const buildBreadcrumbs = (
  t: (key: string) => string,
  matterName: string | undefined,
  matterId: string,
  draftId: string | undefined,
  insightId: string | undefined,
  activeTab: string,
  draftTypeMap: Record<string, any>,
  insightTypeMap: Record<string, any>,
  allDraftTypes: any[],
  allInsightTypes: any[],
  typesLoading: boolean  
) => {
  const breadcrumbs = [
    { name: t('Matters'), href: '/matters', current: false },
    { 
      name: matterName || t('Loading...'), 
      href: `/matters/${matterId}`, 
      current: false
    },
    {
      name: t(activeTab),
      href: `/matters/${matterId}?tab=${activeTab}`,
      current: !draftId && !insightId
    },
  ];

  if (draftId && activeTab === 'Drafts') {
    let draftName = t('Loading...'); 
    
    if (!typesLoading) {  
      const draftType = draftTypeMap[draftId];
      draftName = draftType?.name || draftType?.Name || t('Document');
      
      if (draftType?.parent_id) {
        const parentType = allDraftTypes.find((dt: any) => dt.id === draftType.parent_id);
        if (parentType) {
          draftName = `${parentType.name || parentType.Name} - ${draftName}`;
        }
      }
    }
    
    breadcrumbs.push({
      name: draftName,
      href: `/matters/${matterId}/document/${draftId}?tab=Drafts`,
      current: true
    });
  }

  if (insightId && activeTab === 'Strategy') {
    let insightName = t('Loading...');  
    
    if (!typesLoading) {  
      const insightType = insightTypeMap[insightId];
      insightName = insightType ? formatInsightName(insightType.name || insightType.Name) : t('Insight');
      
      if (insightType?.parent_id) {
        const parentType = allInsightTypes.find((it: any) => it.id === insightType.parent_id);
        if (parentType) {
          insightName = `${formatInsightName(parentType.name || parentType.Name)} - ${insightName}`;
        }
      }
    }
    
    breadcrumbs.push({
      name: insightName,
      href: `/matters/${matterId}/insight/${insightId}?tab=Strategy`,
      current: true
    });
  }

  return breadcrumbs;
};

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-fadePulse bg-gray-200 dark:bg-gray-700 ${className}`}></div>
);

export const getStatusColor = (status: string | undefined | null): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return 'gray';
  }

  const normalizedStatus = status.trim().toLowerCase();
  
  switch (normalizedStatus) {
    case 'draft': return 'gray';
    case 'active': 
    case 'open': return 'green';
    case 'completed': 
    case 'closed': return 'blue';
    case 'under_review': 
    case 'pending': 
    case 'review': return 'yellow';
    case 'archived': return 'purple';
    case 'on_hold': 
    case 'paused': return 'indigo';
    case 'cancelled': 
    case 'rejected': return 'red';
    case 'intake/pre-dispute': 
    case 'intake': 
    case 'pre-dispute': return 'gray';
    case 'evaluation': return 'yellow';
    case 'discovery': return 'blue';
    case 'resolution_process': 
    case 'resolution': return 'green';
    case 'post-resolution': return 'purple';
    default: return 'gray';
  }
};

export const getMatterStatusColor = (
  status: string | undefined | null, 
  matterStatuses: Array<{ label: string; value: string }> = []
): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return 'gray';
  }

  const normalizedStatus = status.trim().toLowerCase();
  
  const statusConfig = matterStatuses.find(s => 
    s.value.toLowerCase() === normalizedStatus || 
    s.label.toLowerCase() === normalizedStatus
  );
  
  if (statusConfig) {
    const colorMap: Record<string, string> = {
      'intake': 'gray',
      'evaluation': 'yellow',
      'discovery': 'blue',
      'resolution_process': 'green',
      'resolution': 'green',
      'post-resolution': 'purple',
    };
    
    return colorMap[statusConfig.value.toLowerCase()] || 'gray';
  }

  return getStatusColor(status);
};

export const getStatusDisplay = (
  status: string | undefined | null, 
  t: (key: string) => string,
  matterStatuses: Array<{ label: string; value: string }> = []
): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return t('Unknown');
  }

  const normalizedStatus = status.trim();
  
  const statusConfig = matterStatuses.find(s => 
    s.value.toLowerCase() === normalizedStatus.toLowerCase() || 
    s.label.toLowerCase() === normalizedStatus.toLowerCase()
  );
  
  if (statusConfig) {
    return statusConfig.label || statusConfig.value;
  }

  return t(normalizedStatus) !== normalizedStatus ? t(normalizedStatus) : normalizedStatus;
};

const getCountryCode = (countryName: string) => {
  return getCode(countryName) || 'UNKNOWN';
};

const getInitials = (name?: string | null): string => {
  if (!name || typeof name !== 'string') return 'U';
  const parts = name.trim().split(' ').filter(p => p.length > 0);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const MatterDetailsSkeleton: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-6 w-20" />
    </div>
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);
// Helper function to determine user's access level for a matter
const getUserAccessLevel = (
  userInfo: any,
  collaborators: any[],
  matterData: MatterData | null
): 'edit' | 'admin' | 'view' => {
  if (!collaborators?.length) {
    return 'view';
  }

  const userUUID = String(userInfo?.UserUID || '').toLowerCase().trim();
  const userID = userInfo?.UserID;

  // First, try to find by collaborator UUID
  let userCollaborator = collaborators.find((collab: any) =>
    String(collab.collaborator_id || '').toLowerCase().trim() === userUUID
  );

  // If found in collaborators, return their access level
  if (userCollaborator) {
    return (userCollaborator.access as 'edit' | 'admin' | 'view') || 'view';
  }

  // If not found in collaborators, check if user is the matter creator
  if (matterData && String(matterData.UserID) === String(userID)) {
    return 'admin'; // Matter creators have admin access
  }

  // Default to view if not found
  return 'view';
};

const MatterDetails: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { matterId, draftId, insightId } = useParams<RouteParams>(); 
  const userType = getUserType();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { hasAiFeatureAccess } = useLicenseGate();

  const [dataState, setDataState] = useState<DataState>({
    data: null,
    lastFetched: null,
    isStale: false,
    error: null,
    hasTriedLoading: false,
  });
  
  const [loadingInitialData, setLoadingInitialData] = useState<boolean>(true);
  const [isEditingMatterInfo, setIsEditingMatterInfo] = useState(false);
  const [editedMatterFields, setEditedMatterFields] = useState<any>({});
  const [allDraftTypes, setAllDraftTypes] = useState<any[]>([]);
  const [allInsightTypes, setAllInsightTypes] = useState<any[]>([]); 
  const [selectedDraftTypeIds, setSelectedDraftTypeIds] = useState<number[]>([]);
  const [selectedInsightTypeIds, setSelectedInsightTypeIds] = useState<number[]>([]); 
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const matterStatuses = useSelector((state: RootState) => state.dropdown.dropdowns.matterStatuses || []);

  const matterData = dataState.data;
  const prefetchedData = useSelector((state: RootState) => state.matters.prefetchedMatterData);

  const queryParams = new URLSearchParams(location.search);
  const initialTab = draftId ? 'Drafts' : insightId ? 'Strategy' : queryParams.get('tab') || 'Details';
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const matterName = matterData?.MatterName || t('Untitled Matter');

  // Keep activeTab in sync with URL params when navigating via deep links
  useEffect(() => {
    if (draftId && activeTab !== 'Drafts') {
      setActiveTab('Drafts');
    } else if (insightId && activeTab !== 'Strategy') {
      setActiveTab('Strategy');
    }
  }, [draftId, insightId]);

  // Clear any stale tour lock from previous pages (e.g. Matters list)
  useEffect(() => {
    clearActiveTourLock();
  }, []);

  // ── Orientation Tour ─────────────────────────────────────────
  const {
    isRunning: isOrientationTourRunning,
    handleJoyrideCallback: handleOrientationTourCallback,
  } = useFeatureTour({
    tourId: 'matter-detail-orientation',
    steps: matterDetailOrientationSteps,
    readyToShow: !loadingInitialData && !!matterData,
    autoStartDelay: 600,
  });

  const handleOrientationBeforeStep = useCallback(
    (step: any) => {
      const tab = step?.data?.tab;
      if (tab && tab !== activeTab) {
        setActiveTab(tab);
      }
    },
    [activeTab],
  );

  const [sources, setSources] = useState<any>(null);
  const [questionnaire, setQuestionnaire] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [suggestedTasks, setSuggestedTasks] = useState<any[]>([]);
  const [matterTasks, setMatterTasks] = useState<any[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [suggestedTasksLoading, setSuggestedTasksLoading] = useState(false);
  const [matterTasksLoading, setMatterTasksLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(true);
  const [userAccessLevel, setUserAccessLevel] = useState<'edit' | 'admin' | 'view'>('view');
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [newCollaboratorAccess, setNewCollaboratorAccess] = useState<'edit' | 'view'>('view');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<{
    last_analyzed_at: string | null;
    processing_status: string | null;
    documents_since_analysis: number;
    has_been_analyzed: boolean;
    context_changed_since_analysis?: boolean;
    correlation_id?: string | null;
  } | null>(null);

  // Data source selection for analysis
  const [availableDataSources, setAvailableDataSources] = useState<{ value: string; label: string }[]>([]);

  // Analysis progress tracking (PubSub-driven)
  const [analysisProgress, setAnalysisProgress] = useState<Step[]>([]);
  const [analysisCorrelationId, setAnalysisCorrelationId] = useState<string | null>(null);
  const [analysisPercentage, setAnalysisPercentage] = useState(0);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const analysisHandlerRef = useRef<((...args: any[]) => void) | null>(null);
  const analysisGroupRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const dataStateRef = useRef<any>(null);
  // Set to true once loadAll (or the prefetch path) has finished — used to guard
  // standalone effects from re-firing immediately after initial load completes.
  const initialLoadDoneRef = useRef(false);

  const people = useSelector((state: RootState) => state.orgPeople?.people || []);

  const draftTypeMap = useMemo(() => {
    return allDraftTypes.reduce((acc: Record<string, any>, dt: any) => {
      const key = dt.id || dt.document_type_id || dt.DraftID;
      if (key) acc[String(key)] = dt;
      return acc;
    }, {} as Record<string, any>);
  }, [allDraftTypes]);

  const insightTypeMap = useMemo(() => { 
    return allInsightTypes.reduce((acc: Record<string, any>, it: any) => {
      const key = it.id || it.document_type_id || it.InsightID;
      if (key) acc[String(key)] = it;
      return acc;
    }, {} as Record<string, any>);
  }, [allInsightTypes]);

  const refreshSummary = useCallback(async (showLoading = false) => {
    if (showLoading) setLoadingInitialData(true);

    try {
      let detailsResult = prefetchedData;

      if (!detailsResult) {
        detailsResult = await dispatch(fetchMatterDetails(matterId));
      }

      if (detailsResult) {
        const matterData = {
          ...detailsResult,
          uuid: matterId,
          MatterName: detailsResult.MatterName || detailsResult.title || '',
          Description: detailsResult.Description || detailsResult.description || '',
          MatterType: detailsResult.MatterType || detailsResult.type || '',
          Jurisdiction: detailsResult.Jurisdiction || detailsResult.jurisdiction || '',
          MatterStage: detailsResult.status_name || detailsResult.stage_name || '',
          created_at: detailsResult.created_at || new Date().toISOString(),
          updated_at: detailsResult.updated_at || detailsResult.UpdatedAt || new Date().toISOString(),
          due_date: detailsResult.due_date || detailsResult.DueDate || null,
          language: detailsResult.language || detailsResult.Language || '',
          collaborators: detailsResult.collaborators || detailsResult.Collaborators || [],
        };
        setDataState({
          data: matterData,
          lastFetched: Date.now(),
          isStale: false,
          error: null,
          hasTriedLoading: true,
        });
        dataStateRef.current = matterData;
        setEditedMatterFields(matterData || {});
        // Transform collaborators from API format to UI format
        const transformedCollaborators = (matterData.collaborators || []).map((collab: any) => {
          return {
            ...collab,
            name: collab.FullName || collab.name || '',
            initials: getInitials(collab.FullName || collab.name),
            email: collab.Email || collab.email,
            avatarURL: collab.ProfilePicture || collab.avatarURL,
          };
        });
        setCollaborators(transformedCollaborators);
        return matterData;
      } else {
        // If detailsResult is null, it means the matter doesn't exist
        // Mark as having tried loading so we don't keep retrying
        setDataState(prev => ({
          ...prev,
          error: 'Matter does not exist or has been deleted',
          hasTriedLoading: true,
        }));
      }
    } catch (err) {
      const errorMessage = (err as any)?.message || t('Failed to load matter data');
      setDataState(prev => ({
        ...prev,
        error: errorMessage,
        hasTriedLoading: true,
      }));
      console.error('Failed to load initial data:', err);
    } finally {
      // Keep loading state if the matter is currently being processed —
      // the processing-detection effect will take over and show the
      // ProgressTracker instead of flashing empty content.
      if (showLoading) {
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      }
    }
    return null;
  }, [matterId, dispatch, t, prefetchedData]);

  const refreshSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const result = await dispatch(fetchMatterSources(matterId));
      if (result) {
        setSources(result);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setSourcesLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshQuestionnaire = useCallback(async () => {
    setQuestionnaireLoading(true);
    try {
      const result = await dispatch(fetchQuestionnaire(matterId));
      if (result) {
        setQuestionnaire(result);
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
    } finally {
      setQuestionnaireLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const result = await dispatch(fetchMatterNotes(matterId));
      if (result) {
        setNotes(result);
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setNotesLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshSuggestedTasks = useCallback(async () => {
    setSuggestedTasksLoading(true);
    try {
      const result = await dispatch(fetchSuggestedTasks(matterId));
      if (result) {
        setSuggestedTasks(result);
      }
    } catch (error) {
      console.error('Failed to load suggested tasks:', error);
    } finally {
      setSuggestedTasksLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshMatterTasks = useCallback(async () => {
    setMatterTasksLoading(true);
    try {
      const result = await dispatch(fetchAllTasks({ matter_uuid: matterId, limit: 100 }));
      if (result?.tasks) {
        setMatterTasks(result.tasks);
      }
    } catch (error) {
      console.error('Failed to load matter tasks:', error);
    } finally {
      setMatterTasksLoading(false);
    }
  }, [matterId, dispatch]);

  // Fetch draft & insight document types for breadcrumb resolution.
  // Called from loadAll so the skeleton waits for these before dropping.
  const loadDocumentTypes = useCallback(async () => {
    try {
      const [draftsRes, insightsRes] = await Promise.all([
        apis.get('/drafts/document-types').catch(() => null),
        apis.get('/insights/document-types').catch(() => null),
      ]);
      if (draftsRes?.data?.documents) {
        setAllDraftTypes(draftsRes.data.documents);
      }
      if (insightsRes?.data?.strategies) {
        setAllInsightTypes(insightsRes.data.strategies);
      }
    } catch (error) {
      console.error('Failed to fetch document types for breadcrumbs:', error);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!matterId) return;

    if (prefetchedData) {
      // Prefetch path: summary already partially available — load the rest in parallel,
      // then drop the skeleton once all critical data (sources, analysis status, doc types) is ready.
      refreshSummary(false).then(async () => {
        await Promise.all([
          refreshSources(),
          dispatch(getAnalysisStatus(matterId)).then((status: any) => {
            if (status) setAnalysisStatus(status);
          }).catch(() => {}),
          loadDocumentTypes(),
        ]);
        initialLoadDoneRef.current = true;
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      });
      setTimeout(() => {
        dispatch(clearPrefetchedMatterData());
      }, 100);
    } else if (!dataState.data && !dataState.hasTriedLoading) {
      // Fresh load: wait for ALL data the user sees immediately on the Details tab before
      // dropping the skeleton. Secondary data (org people, data sources) loads in the
      // background after the skeleton drops — see effects below.
      const loadAll = async () => {
        setLoadingInitialData(true);
        await Promise.all([
          refreshSummary(false),
          refreshSources(),
          dispatch(getAnalysisStatus(matterId)).then((status: any) => {
            if (status) setAnalysisStatus(status);
          }).catch(() => {}),
          loadDocumentTypes(),
        ]);
        initialLoadDoneRef.current = true;
        // Check if matter is being processed — if so, keep skeleton
        // (the processing-detection effect will take over)
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      };
      loadAll();
    }
  }, [matterId, prefetchedData, dispatch, refreshSummary, refreshSources, loadDocumentTypes, dataState.data, dataState.hasTriedLoading]);

  // Fetch org people for collaborator selection — deferred until after the skeleton drops
  // so it doesn't block the critical-path Promise.all in loadAll.
  useEffect(() => {
    if (loadingInitialData) return;
    if (people.length === 0) {
      dispatch(fetchOrgPeople() as any);
    }
  }, [dispatch, people.length, loadingInitialData]);

  // Calculate user access level based on collaborators
  useEffect(() => {
    const accessLevel = getUserAccessLevel(userInfo, collaborators, matterData);
    setUserAccessLevel(accessLevel);
  }, [matterData, userInfo, collaborators]);

  useEffect(() => {
    if (activeTab === 'Documents' && !sources) {
      refreshSources();
    }
    if (activeTab === 'Q&As' && questionnaire.length === 0) {
      refreshQuestionnaire();
    }
    if (activeTab === 'Notes' && notes.length === 0) {
      refreshNotes();
    }
    if (activeTab === 'Tasks' && suggestedTasks.length === 0 && matterTasks.length === 0) {
      refreshSuggestedTasks();
      refreshMatterTasks();
    }
  }, [activeTab, sources, questionnaire.length, notes.length, suggestedTasks.length, matterTasks.length, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  const handleChildUpdate = useCallback(() => {
    refreshSummary(false);
    if (activeTab === 'Documents') {
      refreshSources();
    } else if (activeTab === 'Q&As') {
      refreshQuestionnaire();
    } else if (activeTab === 'Notes') {
      refreshNotes();
    } else if (activeTab === 'Tasks') {
      refreshSuggestedTasks();
      refreshMatterTasks();
    }
  }, [activeTab, refreshSummary, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  // Re-fetch analysis status whenever processing_status changes after initial load.
  // Uses initialLoadDoneRef (not loadingInitialData state) as the guard so that
  // the effect does NOT re-trigger the moment the skeleton drops — only genuine
  // processing_status changes on the matter will cause a re-fetch.
  useEffect(() => {
    if (!initialLoadDoneRef.current) return; // loadAll has not completed yet
    if (matterId) {
      dispatch(getAnalysisStatus(matterId)).then((status: any) => {
        if (status) setAnalysisStatus(status);
      });
    }
  }, [matterId, dispatch, matterData?.processing_status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available data sources for the analyze wizard
  useEffect(() => {
    if (!hasAiFeatureAccess('data_source_indexing')) return;
    const loadDataSources = async () => {
      const dsResult: any = await dispatch(fetchDataSources({ page: 1, limit: 100 }));
      if (dsResult?.dataSources) {
        const readySources = dsResult.dataSources
          .filter((ds: any) => {
            const idxStatus = (ds.indexer_status || '').toLowerCase();
            const dsStatus = (ds.status || '').toLowerCase();
            return (
              ['success', 'completed'].includes(idxStatus) ||
              ['ready', 'active'].includes(dsStatus)
            );
          })
          .map((ds: any) => ({
            value: ds.uuid,
            label: ds.name || 'Unnamed Data Source',
          }));
        setAvailableDataSources(readySources);
      }
    };
    loadDataSources();
  }, [dispatch, hasAiFeatureAccess]);

  // On mount: detect if matter is currently processing and reconnect to PubSub
  useEffect(() => {
    if (!matterData || isAnalysisRunning) return;

    const processingStatus = matterData.processing_status;
    const isCurrentlyProcessing = processingStatus === 'running' || processingStatus === 'queued' || processingStatus === 'processing';

    if (isCurrentlyProcessing) {
      const corrId = matterData.processing_correlation_id || analysisStatus?.correlation_id;
      if (corrId) {
        connectToAnalysisPubSub(corrId);
      } else {
        // No correlation ID available, just show a simple running state
        setIsAnalysisRunning(true);
        setIsAnalyzing(true);
      }
      // Now that analysis tracking is active, release the loading skeleton
      initialLoadDoneRef.current = true;
      setLoadingInitialData(false);
    }
  }, [matterData?.processing_status, matterData?.processing_correlation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup PubSub on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
    };
  }, []);

  // --- Analysis PubSub event handler ---
  const handleAnalysisProgress = useCallback((raw: any) => {
    if (!mountedRef.current) return;

    const event = Array.isArray(raw) ? raw[0] : raw || {};
    const innerData = event.data || {};
    const data = { ...event, ...innerData };

    const rawStatus = data.status ?? '';
    const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
    const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
    const stepName = data.step_name || data.stepName || data.step || null;
    const message = data.message || '';
    const progressPercent = data.progress_percent || 0;

    const isSystemInit = subType === 'matter_initiated' || subType === 'matter.initiated' || (status === 'started' && Array.isArray(data.steps));
    const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
    const isFailure = status === 'failed' || data.error;
    const isWelcome = stepName === 'welcome' || subType === 'welcome' || message?.toLowerCase().includes('preparing');
    const isTechnical = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !message;

    if (isTechnical || isWelcome) return;

    if (isSystemInit) {
      const normalizedSteps: Step[] = (data.steps || []).map((s: any) => ({
        step: s.step || s.step_name || 'Analysis',
        status: 'Pending' as const,
        messages: [],
      }));
      setAnalysisProgress(normalizedSteps);
      if (progressPercent) setAnalysisPercentage(progressPercent);

      const idToUse = data.correlationId || data.correlation_id || analysisCorrelationId;
      if (idToUse && data.startTime) {
        localStorage.setItem(`matter_creation_start:${idToUse}`, data.startTime);
      }
      return;
    }

    if (subType === 'step_started' && stepName) {
      setAnalysisProgress(prev => prev.map(s =>
        s.step === stepName ? { ...s, status: 'In Progress' as const } : s
      ));
      if (progressPercent) setAnalysisPercentage(progressPercent);
      if (message) {
        setAnalysisProgress(prev => prev.map(s =>
          s.step === stepName ? { ...s, messages: [...s.messages, message] } : s
        ));
      }
      return;
    }

    if (subType === 'step_progress' && message) {
      setAnalysisProgress(prev => prev.map(s =>
        s.status === 'In Progress' ? { ...s, messages: [...s.messages, message] } : s
      ));
      if (progressPercent) setAnalysisPercentage(progressPercent);
      return;
    }

    if (subType === 'step_completed' && stepName) {
      setAnalysisProgress(prev => prev.map(s =>
        s.step === stepName ? { ...s, status: 'Completed' as const, messages: message ? [...s.messages, message] : s.messages } : s
      ));
      if (progressPercent) setAnalysisPercentage(progressPercent);
      return;
    }

    if (isCompletion) {
      // Mark any remaining steps as completed
      setAnalysisProgress(prev => prev.map(s =>
        s.status !== 'Completed' ? { ...s, status: 'Completed' as const } : s
      ));
      setAnalysisPercentage(100);
      setAnalysisComplete(true);

      // Clean up PubSub
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
      analysisGroupRef.current = null;
      analysisHandlerRef.current = null;

      // After a short delay, refresh data and only then hide the progress overlay
      setTimeout(async () => {
        if (!mountedRef.current) return;

        // Show the loading skeleton while we fetch fresh data
        setLoadingInitialData(true);

        // Hide progress tracker state (skeleton takes over)
        setIsAnalysisRunning(false);
        setIsAnalyzing(false);
        setAnalysisComplete(false);
        setAnalysisProgress([]);
        setAnalysisPercentage(0);
        setAnalysisCorrelationId(null);

        // Reset stale data so lazy-loading guards allow re-fetch
        setSources(null);
        setQuestionnaire([]);
        setNotes([]);
        setSuggestedTasks([]);
        setMatterTasks([]);

        // Refresh ALL data tabs — analysis may have produced new data everywhere
        // Await the summary so the skeleton stays visible until core data arrives
        await refreshSummary(true);
        refreshSources();
        refreshQuestionnaire();
        refreshNotes();
        refreshSuggestedTasks();
        refreshMatterTasks();
        dispatch(getAnalysisStatus(matterId)).then((s: any) => {
          if (s) setAnalysisStatus(s);
        });
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Analysis completed successfully',
          modalType: 'success',
          duration: 3000,
        }));
      }, 2000);
      return;
    }

    if (isFailure) {
      const errorMsg = data.error || message || 'Analysis failed';
      setAnalysisProgress(prev => prev.map(s =>
        s.status === 'In Progress' ? { ...s, status: 'Failed' as const, messages: [...s.messages, errorMsg] } : s
      ));

      // Clean up PubSub
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
      analysisGroupRef.current = null;
      analysisHandlerRef.current = null;

      setTimeout(() => {
        if (!mountedRef.current) return;
        setIsAnalysisRunning(false);
        setIsAnalyzing(false);
        setAnalysisProgress([]);
        setAnalysisPercentage(0);
        setAnalysisCorrelationId(null);
        refreshSummary(false);
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Analysis failed: ${errorMsg}`,
          modalType: 'error',
          duration: 5000,
        }));
      }, 3000);
      return;
    }
  }, [analysisCorrelationId, dispatch, matterId, refreshSummary, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  // Keep handler ref in sync
  useEffect(() => {
    analysisHandlerRef.current = handleAnalysisProgress;
  }, [handleAnalysisProgress]);

  // --- Connect to PubSub for analysis tracking ---
  const connectToAnalysisPubSub = useCallback(async (correlationId: string) => {
    const multiHubService = getMultiHubSocketService();
    const group = `matter_${correlationId}`;

    try {
      await multiHubService.connectToHub('ai_processing');
      multiHubService.emit('ai_processing', 'join', { group });
      multiHubService.on('ai_processing', '*', handleAnalysisProgress);
      analysisGroupRef.current = group;
      analysisHandlerRef.current = handleAnalysisProgress;

      setAnalysisCorrelationId(correlationId);
      setIsAnalysisRunning(true);
      setIsAnalyzing(true);

      // Store start time for elapsed timer
      localStorage.setItem(`matter_creation_start:${correlationId}`, Date.now().toString());
    } catch (error) {
      console.error('Failed to connect to analysis PubSub:', error);
    }
  }, [handleAnalysisProgress]);

  const handleAnalyzeMatter = useCallback(async (config: AnalyzeWizardResult) => {
    if (!matterId || isAnalyzing) return;
    dispatch(hideModal());
    setIsAnalyzing(true);

    try {
      const result = await dispatch(analyzeMatter(matterId, config.analysisOptions, config.selectedDataSourceUuids, config.selectedIndexes));
      if (result) {
        const correlationId = result.correlationId || result.correlation_id;
        if (correlationId) {
          await connectToAnalysisPubSub(correlationId);
        } else {
          // Fallback: no correlation ID, just poll status
          refreshSummary(false);
          setIsAnalyzing(false);
        }
      } else {
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Failed to analyze matter:', error);
      setIsAnalyzing(false);
    }
  }, [matterId, isAnalyzing, dispatch, connectToAnalysisPubSub, refreshSummary]);

  const openAnalyzeWizard = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Analyze Matter'),
      icon: <PiLightningLight className="w-6 h-6 text-gray-700" />,
      width: '640px',
      content: (
        <AnalyzeWizardModal
          jurisdiction={matterData?.jurisdiction || matterData?.Jurisdiction || ''}
          hasBeenAnalyzed={analysisStatus?.has_been_analyzed || false}
          availableDataSources={availableDataSources}
          onLaunch={handleAnalyzeMatter}
          onClose={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => dispatch(hideModal()),
    }));
  }, [dispatch, t, matterData, analysisStatus, availableDataSources, handleAnalyzeMatter]);

  const handleTabClick = useCallback((tabValue: string) => {
    setActiveTab(tabValue);
    history.push(`/matters/${matterId}?tab=${tabValue}`);
  }, [history, matterId]);

  const getCurrentStatusColor = useCallback(() => {
    if (!matterData?.MatterStage) return 'gray' as BadgeColor;
    return getMatterStatusColor(matterData.MatterStage, matterStatuses) as BadgeColor;
  }, [matterData?.MatterStage, matterStatuses]);

  const getCurrentStatusDisplay = useCallback(() => {
    if (!matterData?.MatterStage) return t('Unknown');
    return getStatusDisplay(matterData.MatterStage, t, matterStatuses);
  }, [matterData?.MatterStage, matterStatuses, t]);

  const handleDeleteMatter = useCallback(() => {
    dispatch(
      showModal({
        showModal: true,
        type: 'custom',
        message: t('Are you sure you want to delete this matter?'),
        subMsg: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        content: (
          <DeleteMatterConfirmation
            matterCount={1}
            onConfirm={async ({ deleteTasks, deleteFilesFolders }) => {
              try {
                const deleteResult = await dispatch(deleteMatter(matterId, { deleteTasks, deleteFilesFolders }));
                if (!deleteResult) return; // Action failed — handleApiError already showed the error toast
                history.push('/matters');
              } catch (err: any) {
                dispatch(showFeedbackModal({
                  modalType: 'error',
                  message: t('Failed to delete matter'),
                  showModal: true,
                  duration: 3000
                }));
                console.error('Failed to delete matter:', err);
              }
              dispatch(hideModal());
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [dispatch, matterId, t, history]);

  const handleSaveMatterInfo = useCallback(async () => {
    try {
      // Only send fields the user actually changed (diff against current matterData)
      const changedFields: Record<string, any> = {};

      if (editedMatterFields.MatterName !== matterData?.MatterName) {
        changedFields.MatterName = editedMatterFields.MatterName;
      }
      if (editedMatterFields.MatterType !== matterData?.MatterType) {
        changedFields.MatterType = editedMatterFields.MatterType;
      }
      if (editedMatterFields.Description !== matterData?.Description) {
        changedFields.Description = editedMatterFields.Description;
      }
      if ((editedMatterFields as any).due_date !== (matterData as any)?.due_date) {
        changedFields.due_date = (editedMatterFields as any).due_date;
      }

      // If nothing changed, just exit — no API call needed
      if (Object.keys(changedFields).length === 0) {
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: t('No changes to save'),
          showModal: true,
          duration: 2000
        }));
        return;
      }

      const editResult = await dispatch(editMatter(matterId, changedFields));
      if (!editResult) return; // Action failed — handleApiError already showed the error toast

      const result = await refreshSummary(false);
      if (result) {
        setEditedMatterFields(result);
      }

      dispatch(showFeedbackModal({
        modalType: 'success',
        message: t('Matter updated successfully'),
        showModal: true,
        duration: 3000
      }));

    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Failed to update matter'),
        showModal: true,
        duration: 3000
      }));
      console.error('Failed to update matter:', error);
      throw error;
    }
  }, [dispatch, matterId, editedMatterFields, matterData, t, refreshSummary]);

  const handleGrantClientAccess = useCallback(async () => {
    try {
      await dispatch(grantClientAccess(matterId));
      await refreshSummary(false);
    } catch (error) {
      console.error('Failed to grant client access:', error);
    }
  }, [dispatch, matterId, refreshSummary]);

  const handleAddCollaborator = useCallback(async () => {
    if (!newCollaboratorEmail.trim() || !matterId) return;

    try {
      const selectedPerson = people.find((person) => person.email === newCollaboratorEmail);
      if (!selectedPerson) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: t('Person not found'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      const alreadyExists = collaborators.some((collab: any) => collab.email === newCollaboratorEmail);
      if (alreadyExists) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: t('This person is already a collaborator'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      const initials = `${(selectedPerson?.first_name || '').charAt(0).toUpperCase()}${(selectedPerson?.last_name || '').charAt(0).toUpperCase()}`.trim() || 'U';
      const fullName = selectedPerson.name || `${selectedPerson?.first_name || ''} ${selectedPerson?.last_name || ''}`.trim() || selectedPerson.email;

      const collaboratorData = {
        MatterID: matterId,
        Collaborators: [{
          id: selectedPerson.user_id || selectedPerson.id,
          name: fullName,
          email: newCollaboratorEmail,
          initials: initials,
          avatarUrl: null,
          role: newCollaboratorAccess === 'edit' ? 'editor' : 'viewer',
        }],
        Permission: (newCollaboratorAccess === 'edit' ? 'write' : 'read') as 'write' | 'read',
      };

      await dispatch(addMatterCollaborators(collaboratorData) as any);
      setNewCollaboratorEmail('');
      setNewCollaboratorAccess('view');
      await refreshSummary(false);
    } catch (error: any) {
      console.error('Error adding collaborator:', error);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error adding collaborator. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [newCollaboratorEmail, newCollaboratorAccess, people, matterId, collaborators, dispatch, t, refreshSummary]);

  const matterBreadcrumbs = useMemo(() => 
    buildBreadcrumbs(t, matterName, matterId, draftId, insightId, activeTab, draftTypeMap, insightTypeMap, allDraftTypes, allInsightTypes, typesLoading),  
    [t, matterName, matterId, draftId, insightId, activeTab, draftTypeMap, insightTypeMap, allDraftTypes, allInsightTypes, typesLoading]  
  );

  const matterDropdownActions: Action[] = useMemo(() => {
    
    return [
      {
        label: t('Manage Access'),
        onClick: () => {
          dispatch(showSidePanel({
            type: 'manageResourceCollaborators',
            showSidePanel: true,
            width: '50%',
            message: t('Manage Access'),
            resourceUuid: matterId,
            resourceType: 'matter',
            collaborators: collaborators.map((c: any) => ({
              ...c,
              user_id: c.collaborator_id || c.user_id || c.UID,
              collaborator_uuid: c.collaborator_id || c.collaborator_uuid || c.UID,
              access: c.access || c.role || c.Role || 'view',
            })),
            currentUserUuid: userInfo?.UserUID,
            canAdmin: true,
            addCollaborator: async (uuid: string, userUuid: string, access: string) => {
              await dispatch(addMatterCollaborators({ MatterID: uuid, Collaborators: [{ id: userUuid, name: '', email: '', initials: '', avatarUrl: null, role: '' }], Permission: access as any }) as any);
            },
            removeCollaborator: async (uuid: string, collabUuid: string) => {
              await dispatch(removeMatterCollaborator(uuid, collabUuid) as any);
            },
            updateAccess: async (uuid: string, collabUuid: string, access: string) => {
              await dispatch(updateMatterCollaboratorRole(uuid, collabUuid, access) as any);
            },
            onUpdate: async () => {
              await refreshSummary(false);
            },
          }));
        },
        icon: <PiUsersLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline',
      },
      {
        label: t('Activity log'),
        onClick: () => setShowAuditLog(prev => !prev),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline',
      },
      {
        label: t('Delete'),
        onClick: handleDeleteMatter, 
        icon: <PiTrashLight className="w-4 h-4 text-red-900" />,
        variant: 'destructive',
      },
    ];
  }, [userType, t, dispatch, matterId, matterData, matterName, isBetaEnv, handleGrantClientAccess, handleDeleteMatter, handleChildUpdate, collaborators, userInfo?.UserUID, refreshSummary]);


  // Check if matter has user-uploaded documents (for gating the Analyze button).
  // AI-retrieved laws and cases do NOT count — the user must provide their own context.
  const hasDocuments = useMemo(() => {
    if (!sources) return false;
    const docs = sources.user_documents || sources.documents || sources.files || [];
    return docs.length > 0;
  }, [sources]);

  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-3 items-center">
      <ProjectBadges resourceType="matter" resourceUuid={matterId} />
      <PendingAccessRequests resourceType="matter" resourceUuid={matterId} />
      {userType && userType.includes('Law') && matterData?.Clients?.length && !isBetaEnv && (
        <Badge color="green" className="flex items-center gap-1">
          <PiCheckCircleLight className="h-4 w-4" />
          {t('Client Has Access')}
        </Badge>
      )}
      {/* Context change indicator — next to the Analyse button for visibility */}
      {!isAnalysisRunning && analysisStatus?.context_changed_since_analysis && analysisStatus?.has_been_analyzed && (
        <Badge color="yellow" icon={<PiWarningLight className="h-3.5 w-3.5" />}>
          {t('Context changed')}
        </Badge>
      )}
      {/* Analyze Matter button — opens wizard modal */}
      {isAnalyzing || isAnalysisRunning || matterData?.processing_status === 'running' ? (
        <div data-tour-id="matter-analyze-button">
        <Button
          variant="outline"
          size="small"
          disabled
          icon={<PiSpinnerLight className="h-4 w-4 animate-spin" />}
        >
          {t('Analyzing...')}
        </Button>
        </div>
      ) : !hasDocuments ? (
        <div data-tour-id="matter-analyze-button">
        <Button
          variant="primary"
          size="small"
          disabled
          tooltip={t('Upload at least one document to run analysis. AI-retrieved laws and cases are not sufficient.')}
          icon={<PiLightningLight className="h-4 w-4" />}
        >
          {analysisStatus?.has_been_analyzed ? t('Re-analyze') : t('Analyze')}
        </Button>
        </div>
      ) : (
        <div data-tour-id="matter-analyze-button">
        <Button
          variant="primary"
          size="small"
          onClick={openAnalyzeWizard}
          icon={<PiLightningLight className="h-4 w-4" />}
        >
          {analysisStatus?.has_been_analyzed ? t('Re-analyze') : t('Analyze')}
        </Button>
        </div>
      )}
      {/* View Strategy link */}
      {/* <Button
        variant="outline"
        size="small"
        onClick={() => history.push(`/strategy/${matterId}`)}
        icon={<PiLightbulbLight className="h-4 w-4" />}
      >
        {t('Strategy')}
      </Button> */}
      {/* {userType && userType.includes('Law') ? ( */}
            {collaborators?.length > 0 && (
        <div data-tour-id="matter-collaborators" className="flex items-center">
          {collaborators.slice(0, 3).map((collab: any, idx: number) => (
            <div key={idx} className={clsx('relative rounded-full', idx > 0 && 'ml-[-0.5rem]')}>
              <CollaboratorBadge collaborator={{
                ...collab,
                access: collab.access,
              }} />
            </div>
          ))}
          {collaborators.length > 3 && (
            <div className="relative ml-[-0.5rem]" title={collaborators.slice(3).map((c: any) => `${c.name || c.FullName} (${c.access || 'view'})`).join(', ')}>
              <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm text-gray-700 dark:text-white font-medium">
                +{collaborators.length - 3}
              </div>
            </div>
          )}
        </div>
      )}
        <Menu as="div" className="relative inline-block text-left z-50" data-tour-id="matter-actions-menu">
          <Menu.Button as={Button} variant="outline" size="small" className="!h-6 !w-6 !p-0 flex items-center justify-center">
            <PiDotsThreeVerticalLight className="h-4 w-4" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
              <div className="px-1 py-1">
                {matterDropdownActions.map((action, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        } group flex w-full items-center rounded-md px-2 py-2 text-sm flex items-center gap-2 ${
                          action.variant === 'destructive' ? 'text-red-700' : ''
                        }`}
                        onClick={action.onClick}
                      >
                        {action.icon}
                        {t(action.label)}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      {/* // ) : (
      //   <Button variant="primary" size="small" onClick={() => {}}>
      //     {t('Find a Lawyer')}
      //   </Button>
      // )} */}
    </div>
  ), [collaborators, isAnalyzing, isAnalysisRunning, analysisStatus, t, handleAnalyzeMatter, openAnalyzeWizard, matterData, matterId, hasDocuments, showAuditLog]);

  const subHeaderRight = useMemo(() => {
    if (!matterData) return null;

    const jurisdictionData = getJurisdictionDisplay(matterData.jurisdiction);

    return (
      <div data-tour-id="matter-status-badges" className="flex flex-row w-full justify-between items-center flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            className="capitalize"
            tooltip={t('Status')}
            color={getCurrentStatusColor()}
          >
            {getCurrentStatusDisplay()}
          </Badge>

          {/* Processing/Analysis status indicator */}
          {matterData.processing_status === 'running' && (
            <Badge color="blue" tooltip={t('AI analysis is running')}>
              <PiSpinnerLight className="h-3 w-3 animate-spin mr-1 inline" />
              {t('Analyzing')}
            </Badge>
          )}
          {analysisStatus?.has_been_analyzed && matterData.processing_status !== 'running' && (
            <Badge color="green" tooltip={t('Last analyzed: ') + (analysisStatus.last_analyzed_at || '')}>
              <PiLightningLight className="h-3 w-3 mr-1 inline" />
              {t('Analyzed')}
            </Badge>
          )}

          {userType && userType.includes('Law') && matterData.Clients?.[0] && (
            <Badge color="indigo" tooltip={t('Client')}>
              {matterData.Clients[0].ClientName || t('No Client')}
            </Badge>
          )}

          <Badge color="gray" tooltip={t('Date Created')}>
            {formatDate(matterData.created_at) || '-'}
          </Badge>

          <Badge color="gray" tooltip={t('Jurisdiction')}>
            {matterData.jurisdiction && jurisdictionData && (
              <Flag
                countryCode={jurisdictionData.countryCode}
                code={jurisdictionData.code}
                size="sm"
              />
            )}
            {matterData.jurisdiction || '-'}
          </Badge>

          <Badge color="gray" tooltip={t('Language')}>
            {matterData.language || '-'}
          </Badge>
        </div>
      </div>
    );
  }, [matterData, userType, t, getCurrentStatusColor, getCurrentStatusDisplay, analysisStatus]);

  const tabs: TabItem[] = useMemo(
    () => [
      {
        label: t('Details'),
        value: 'Details',
        icon: <PiFileTextLight className="h-4 w-4" />,
        position: 'left',
        tooltip: t('Matter information and progress')
      },
      {
        label: t('Documents'),
        value: 'Documents',
        icon: <PiFileLight className="h-4 w-4" />,
        position: 'left',
        tooltip: t('Uploaded files and retrieved legal sources')
      },
      {
        label: t('Q&As'),
        value: 'Q&As',
        icon: <PiQuestionMarkLight className="h-4 w-4" />,
        position: 'left',
        tooltip: t('Questionnaire and client questions')
      },
      {
        label: t('Tasks'),
        value: 'Tasks',
        icon: <PiCheckSquareOffsetLight className="h-4 w-4" />,
        position: 'left',
        tooltip: t('Suggested and active tasks for this matter')
      },
      {
        label: t('Notes'),
        value: 'Notes',
        icon: <PiNoteLight className="h-4 w-4" />,
        position: 'left',
        tooltip: t('Notes and activity history')
      },
      {
        label: 'Drafts',
        renderedLabel: !hasAiFeatureAccess('drafts')
          ? <span className="flex items-center gap-1">{t('Drafts')}<PiLockSimpleLight className="h-3 w-3 text-gray-400" /></span>
          : undefined,
        value: 'Drafts',
        icon: <PiFileTextLight className="h-4 w-4" />,
        position: 'right',
        tooltip: !hasAiFeatureAccess('drafts') ? t('Upgrade your plan to use AI Drafts.') : t('Generate and manage legal drafts')
      },
      {
        label: 'Strategy',
        renderedLabel: !hasAiFeatureAccess('insights')
          ? <span className="flex items-center gap-1">{t('Strategy')}<PiLockSimpleLight className="h-3 w-3 text-gray-400" /></span>
          : undefined,
        value: 'Strategy',
        icon: <PiLightbulbLight className="h-4 w-4" />,
        position: 'right',
        tooltip: !hasAiFeatureAccess('insights') ? t('Upgrade your plan to use AI Insights.') : t('AI-generated legal insights and analysis')
      },
    ],
    [t, hasAiFeatureAccess]
  );

  if (loadingInitialData) return <MatterDetailsSkeleton />;

  // If we've tried loading and have an error, show error state
  if (dataState.error && dataState.hasTriedLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('Matter Not Found')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('The matter you are looking for does not exist or you do not have access to it.')}
          </p>
          <Button
            onClick={() => history.push('/matters')}
            variant='primary'
            size='small'
            className='mx-auto'
          >
            {t('Back to Matters')}
          </Button>
        </div>
      </div>
    );
  }

  // If data failed to load and we haven't tried, show skeleton
  if (!matterData && dataState.hasTriedLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('Matter Not Found')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('The matter you are looking for does not exist or you do not have access to it.')}
          </p>
          <Button
            onClick={() => history.push('/matters')}
            className="mx-auto"
            variant='primary'
            size='small'
          >
            {t('Back to Matters')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <DetailFrame
      breadcrumbs={matterBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
    >
      <div className="flex-1 flex flex-col">
        {/* Analysis Progress Tracker — shows when analysis is running */}
        {isAnalysisRunning && (
          <div className="flex-1 flex items-center justify-center">
            <ProgressTracker
              steps={analysisProgress}
              isComplete={analysisComplete}
              title={t('Analyzing Matter')}
              matterId={matterId}
              correlationId={analysisCorrelationId || undefined}
              isRunning={isAnalysisRunning}
              backendPercentage={analysisPercentage}
            />
          </div>
        )}
        {/* Normal matter content — hidden during analysis */}
        {/* Context change message moved to header, next to Analyse button */}
        {!isAnalysisRunning && (
          <>
            <div data-tour-id="matter-tab-bar" className="pt-2 dark:bg-gray-800 w-full relative overflow-visible z-10 border-b border-gray-200 dark:border-gray-700">
              <MatterTabBar
                tabs={tabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
                showLabels={true}
                allDraftTypes={allDraftTypes}
                selectedDraftTypeIds={selectedDraftTypeIds}
                allInsightTypes={allInsightTypes}
                selectedInsightTypeIds={selectedInsightTypeIds}
              />
            </div>
            <div className="max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar-main flex-1">
                <MatterDetailsContent
                  activeTab={activeTab}
                  matterData={matterData}
                  userType={userType}
                  userAccessLevel={userAccessLevel}
                  isEditingMatterInfo={isEditingMatterInfo}
                  setIsEditingMatterInfo={setIsEditingMatterInfo}
                  editedMatterFields={editedMatterFields}
                  setEditedMatterFields={setEditedMatterFields}
                  handleSaveMatterInfo={handleSaveMatterInfo}
                  onUpdate={handleChildUpdate}
                  matterStatuses={matterStatuses}
                  sources={sources}
                  sourcesLoading={sourcesLoading}
                  questionnaire={questionnaire}
                  questionnaireLoading={questionnaireLoading}
                  notes={notes}
                  setNotes={setNotes}
                  notesLoading={notesLoading}
                  setSources={setSources}
                  collaborators={collaborators}
                  clients={matterData?.matter_clients?.clients || []}
                  suggestedTasks={suggestedTasks}
                  setSuggestedTasks={setSuggestedTasks}
                  matterTasks={matterTasks}
                  setMatterTasks={setMatterTasks}
                  suggestedTasksLoading={suggestedTasksLoading}
                  matterTasksLoading={matterTasksLoading}
                  hasDocuments={hasDocuments}
                />
            </div>
          </>
        )}
      </div>
    </DetailFrame>

    {/* Matter Detail Orientation Tour */}
    <FeatureTour
      steps={matterDetailOrientationSteps}
      run={isOrientationTourRunning}
      onCallback={handleOrientationTourCallback}
      onBeforeStep={handleOrientationBeforeStep}
      disableOverlay
    />

    {/* Audit Log Side Panel with Backdrop */}
    {showAuditLog && (
      <>
        <div
          className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
          onClick={() => setShowAuditLog(false)}
        />
        <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
          <AuditLogPanel
            entityType="matter"
            entityId={matterId}
            entityName={matterData?.MatterName || ''}
            onClose={() => setShowAuditLog(false)}
          />
        </div>
      </>
    )}
    </>
  );
};

export default MatterDetails;
